import { Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogContentText, Grid, TextField, Autocomplete, FormControlLabel, Switch, AppBar, Toolbar, Typography, debounce, IconButton, InputAdornment, Chip, Box } from "@mui/material"
import { useAppDispatch, useAppSelector } from "../../../../../hooks/store-hooks"
import { useUsuariosService } from "../../../../../../use-cases/usuarios"
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { ITipoUsuario, tipoUsuarioOptions, tipoUsuarioToLabel } from "../../../../../../application/models";
import { useEffect, useRef, useState } from "react";
import { useUIService } from "../../../../../../use-cases/ui";
import { useNavigationService } from "../../../../../../use-cases/navigation";
import { setEditUsuarioFormIdsClientesState, setEditUsuarioFormBuscaDisponiveisState, setEditUsuarioFormBuscaSelecionadosState, resetEditUsuarioFormState } from "../../../../../../application/states/usuarios";
import { VisualizacaoClientesAccordion } from './VisualizacaoClientesAccordion';

export const FormEditUsuario = () => {
  // State
  const { editUsuarioForm, clienteSelecionado, clientesVendedorVeles } = useAppSelector((state) => state.usuarios)
  const { perfis } = useAppSelector((state) => state.perfil)
  const { currentUser } = useAppSelector((state) => state.user)
  const { idVelesCliente } = useAppSelector((state) => state.auth)
  const targetVelesClienteId = idVelesCliente

  // Permissões baseadas no tipo de perfil
  const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS
  const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO
  const isCliente = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE

  // Regras de visualização
  const canSeePassword = isDemais || isTecnologiaDaInformacao
  const canSendRecoveryEmail = isDemais || isTecnologiaDaInformacao || isCliente
  const canEdit = isDemais || isTecnologiaDaInformacao || isCliente
  const isEditingTiUser = editUsuarioForm.fields.tipoUsuario === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO
  const isExperienciaOuSupervisor =
    editUsuarioForm.fields.tipoUsuario === ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE ||
    editUsuarioForm.fields.tipoUsuario === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL
  const canChangeProfileType = canEdit && (!isEditingTiUser || isTecnologiaDaInformacao)
  const optionsTipoUsuario = isTecnologiaDaInformacao
    ? tipoUsuarioOptions
    : tipoUsuarioOptions.filter(opt => opt !== ITipoUsuario.TECNOLOGIA_DA_INFORMACAO)
  // Use State
  const [loading, setLoading] = useState<boolean>(true)
  const [carregandoDados, setCarregandoDados] = useState<boolean>(true)
  const [senhaDescriptografada, setSenhaDescriptografada] = useState<string>('')
  const [carregandoSenha, setCarregandoSenha] = useState<boolean>(false)
  const [mostrarSenha, setMostrarSenha] = useState<boolean>(false)
  // Campos de busca via state global
  const buscaDisponiveis = editUsuarioForm.fields.buscaDisponiveis || ''
  const buscaSelecionados = editUsuarioForm.fields.buscaSelecionados || ''
  // Use Ref
  const isFirstRender = useRef(false)
  // Service
  const dispatch = useAppDispatch()
  const {
    setOpenEditUsuarioForm,
    setEditUsuarioFormEmail,
    setEditUsuarioFormNome,
    updateUsuario,
    setEditUsuarioFormAtivo,
    setEditUsuarioFormIdPerfil,
    setEditUsuarioFormTipoUsuario,
    sendRecoveryPasswordEmail,
    setEditUsuarioFormTokenAtivo,
    getUsuarioByIdAndUpdateForm,
    getSenhaDescriptografada,
    generateToken,
    setEditUsuarioFormToken,
    setEditUsuarioFormLogin,
    getClientesVendedorVeles
  } = useUsuariosService()
  const { setAlert, setLoading: setUILoading } = useUIService()
  const { navigateTo } = useNavigationService()
  // useEffect - Carrega dados atualizados do usuário quando abre o formulário
  useEffect(() => {
    if (!isFirstRender.current) {
      setLoading(false)
      isFirstRender.current = true

      if (isDemais || isTecnologiaDaInformacao) {
        getClientesVendedorVeles()
      }

      if (!editUsuarioForm.fields.idUsuario || editUsuarioForm.fields.idUsuario === 0) {
        setAlert({
          message: 'Você deve selecionar um usuário',
          type: 'error'
        })

        navigateTo({
          route: '/dashboard/cadastro/usuario'
        })
      } else {
        // Buscar dados atualizados do usuário ao abrir o formulário
        carregarDadosAtualizadosUsuario().then(() => {
          // Busca e descriptografa a senha do usuário apenas se tiver permissão
          if (canSeePassword) {
            carregarSenhaDescriptografada()
          }
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editUsuarioForm.fields.idUsuario, canSeePassword, isDemais, isTecnologiaDaInformacao, getClientesVendedorVeles, dispatch])


  // Função para carregar dados atualizados do usuário
  const carregarDadosAtualizadosUsuario = async (): Promise<void> => {
    if (!editUsuarioForm.fields.idUsuario || editUsuarioForm.fields.idUsuario === 0) {
      setCarregandoDados(false)
      return
    }

    try {
      await getUsuarioByIdAndUpdateForm(editUsuarioForm.fields.idUsuario)
    } catch (error: any) {
      console.error('Erro ao carregar dados do usuário:', error)
      // Não mostrar erro ao usuário, apenas usar os dados da lista
    } finally {
      setCarregandoDados(false)
    }
  }

  // Função para carregar e descriptografar a senha
  const carregarSenhaDescriptografada = async () => {
    if (!editUsuarioForm.fields.idUsuario || editUsuarioForm.fields.idUsuario === 0) {
      return
    }

    setCarregandoSenha(true)
    try {
      const senhaDescriptografada = await getSenhaDescriptografada(editUsuarioForm.fields.idUsuario)
      setSenhaDescriptografada(senhaDescriptografada)
    } catch (error: any) {
      console.error('Erro ao carregar senha:', error)
      setSenhaDescriptografada('')
      const backendMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message
      setAlert({
        message: backendMessage ? `Erro ao carregar senha: ${backendMessage}` : 'Erro ao carregar senha do usuário',
        type: 'warning'
      })
    } finally {
      setCarregandoSenha(false)
    }
  }
  // Function
  const handleClose = () => {
    setOpenEditUsuarioForm({
      open: false
    })
  }

  return (
    loading || carregandoDados ? <></>
      : <>
        <Dialog
          open={true}
          fullScreen
          disableEscapeKeyDown
          onClose={handleClose}
          scroll={'paper'}
          aria-labelledby="scroll-dialog-title"
          aria-describedby="scroll-dialog-description"
          PaperProps={{
            component: 'form',
            onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              updateUsuario();
            },
          }}
        >
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar>
              <Typography sx={{ ml: 2, flex: 1 }} textTransform={"uppercase"} fontWeight={"bold"} variant="h6" component="div">
                Editar Usuário
              </Typography>
              <Button
                color="inherit"
                startIcon={<CloseIcon />}
                onClick={handleClose}
                aria-label="close"
              >
                Fechar
              </Button>
            </Toolbar>
          </AppBar>
          <DialogContent dividers={true}>
            <DialogContentText id="scroll-dialog-description" tabIndex={-1}></DialogContentText>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  defaultValue={editUsuarioForm.fields.nome}
                  onChange={debounce((e) => {
                    setEditUsuarioFormNome({
                      nome: e.target.value
                    })
                  }, 500)}
                  disabled={!canEdit}
                  error={Boolean(editUsuarioForm.touched && editUsuarioForm.errors.find(item => item.key === 'nome'))}
                  helperText={editUsuarioForm.errors.find(item => item.key === 'nome')?.message}
                  name="nome"
                  fullWidth
                  id="nome"
                  label="Nome"
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  defaultValue={editUsuarioForm.fields.email}
                  onChange={debounce((e) => {
                    setEditUsuarioFormEmail({
                      email: e.target.value
                    })
                  }, 500)}
                  disabled={!canEdit}
                  error={Boolean(editUsuarioForm.touched && editUsuarioForm.errors.find(item => item.key === 'email'))}
                  helperText={editUsuarioForm.errors.find(item => item.key === 'email')?.message}
                  name="email"
                  fullWidth
                  id="email"
                  label="E-mail"
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  defaultValue={editUsuarioForm.fields.login}
                  disabled={!canEdit}
                  onChange={debounce((e) => {
                    setEditUsuarioFormLogin({
                      login: e.target.value
                    })
                  }, 500)}
                  name="login"
                  fullWidth
                  id="login"
                  label="Login"
                />
              </Grid>
              {
                // Campo de senha: apenas Veles Demais pode ver
                canSeePassword && (
                  <Grid item xs={12}>
                    <TextField
                      type={mostrarSenha ? "text" : "password"}
                      value={senhaDescriptografada}
                      disabled={true}
                      name="senha"
                      fullWidth
                      id="senha"
                      label="Senha do Usuário"
                      helperText={carregandoSenha ? "Carregando senha..." : "Senha descriptografada (somente visualização)"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setMostrarSenha(!mostrarSenha)}
                              edge="end"
                            >
                              {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )
              }
              {
                // Botão de recuperação: Veles Demais e Cliente podem enviar
                canSendRecoveryEmail && (
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onClick={() => {
                        if (editUsuarioForm.fields.idUsuario && editUsuarioForm.fields.idUsuario > 0) {
                          sendRecoveryPasswordEmail(editUsuarioForm.fields.idUsuario)
                        }
                      }}
                      disabled={!editUsuarioForm.fields.idUsuario || editUsuarioForm.fields.idUsuario === 0}
                    >
                      Esqueceu a senha?
                    </Button>
                  </Grid>
                )
              }
              <Grid item xs={12}>
                <Autocomplete
                  options={
                    (() => {
                      // Filtrar perfis e remover duplicatas por IdPerfil
                      const perfisFiltrados = editUsuarioForm.fields.idPerfil
                        ? perfis.filter(p => p.Ativo || p.IdPerfil === editUsuarioForm.fields.idPerfil)
                        : perfis.filter(p => p.Ativo);

                      // Remover duplicatas baseado em IdPerfil
                      const perfisUnicos = perfisFiltrados.filter((perfil, index, self) =>
                        index === self.findIndex(p => p.IdPerfil === perfil.IdPerfil)
                      );

                      return perfisUnicos;
                    })()
                  }
                  getOptionLabel={(option) => option.Descricao}
                  isOptionEqualToValue={(option, value) => option.IdPerfil === value.IdPerfil}
                  renderOption={(props, option) => (
                    <li {...props} key={option.IdPerfil}>
                      {option.Descricao}
                    </li>
                  )}
                  disabled={!canEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Perfil"
                      fullWidth
                      error={Boolean(editUsuarioForm.touched && editUsuarioForm.errors.find(item => item.key === 'idPerfil'))}
                      helperText={editUsuarioForm.errors.find(item => item.key === 'idPerfil')?.message}
                    />
                  )}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setEditUsuarioFormIdPerfil({
                        idPerfil: newValue.IdPerfil
                      })
                    } else {
                      setEditUsuarioFormIdPerfil({
                        idPerfil: 0
                      })
                    }
                  }}
                  value={perfis.find((perfil) => perfil.IdPerfil === editUsuarioForm.fields.idPerfil) || null}
                />
              </Grid>
              {
                currentUser?.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE && clienteSelecionado?.IdCliente === currentUser?.IdCliente && (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={optionsTipoUsuario}
                      getOptionLabel={(option) => tipoUsuarioToLabel(option).toUpperCase()}
                      disabled={!canChangeProfileType}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tipo de Usuário"
                          value={editUsuarioForm.fields.tipoUsuario ? tipoUsuarioToLabel(tipoUsuarioOptions.find((tu) => tu === editUsuarioForm.fields.tipoUsuario)!).toUpperCase() : ''}
                          fullWidth
                          error={Boolean(editUsuarioForm.touched && editUsuarioForm.errors.find(item => item.key === 'tipoUsuario'))}
                          helperText={editUsuarioForm.errors.find(item => item.key === 'tipoUsuario')?.message}
                        />
                      )}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setEditUsuarioFormTipoUsuario({
                            tipoUsuario: newValue
                          })
                        } else {
                          setEditUsuarioFormTipoUsuario({
                            tipoUsuario: undefined as any
                          })
                        }
                      }}
                      value={tipoUsuarioOptions.find((tu) => tu === editUsuarioForm.fields.tipoUsuario) || null}
                    />
                  </Grid>
                )
              }

              {(isDemais || isTecnologiaDaInformacao) && (clienteSelecionado?.IdCliente === targetVelesClienteId) && isExperienciaOuSupervisor && (
                <Grid item xs={12}>
                  <VisualizacaoClientesAccordion
                    idsClientes={editUsuarioForm.fields.idsClientes || []}
                    buscaDisponiveis={buscaDisponiveis}
                    buscaSelecionados={buscaSelecionados}
                    clientesVendedorVeles={clientesVendedorVeles}
                    canEdit={canEdit}
                    onAddCliente={(idCliente) =>
                      dispatch(setEditUsuarioFormIdsClientesState({
                        idsClientes: [...(editUsuarioForm.fields.idsClientes || []), idCliente]
                      }))
                    }
                    onRemoveCliente={(idCliente) =>
                      dispatch(setEditUsuarioFormIdsClientesState({
                        idsClientes: (editUsuarioForm.fields.idsClientes || []).filter(id => id !== idCliente)
                      }))
                    }
                    onLimparTodos={() =>
                      dispatch(setEditUsuarioFormIdsClientesState({ idsClientes: [] }))
                    }
                    onBuscaDisponiveisChange={(value) =>
                      dispatch(setEditUsuarioFormBuscaDisponiveisState({ buscaDisponiveis: value }))
                    }
                    onBuscaSelecionadosChange={(value) =>
                      dispatch(setEditUsuarioFormBuscaSelecionadosState({ buscaSelecionados: value }))
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ overflow: 'visible' }}>
                  <CardHeader
                    title="Token de API"
                    subheader="Ative e gerencie o token para integração via API."
                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                    subheaderTypographyProps={{ variant: 'body2' }}
                  />
                  <CardContent sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      disabled={!canEdit}
                      control={<Switch
                        checked={editUsuarioForm.fields.tokenAtivo || false}
                        onChange={async (e) => {
                          const novoTokenAtivo = e.target.checked

                          if (novoTokenAtivo) {
                            // Se estiver ativando
                            if (!editUsuarioForm.fields.token || editUsuarioForm.fields.token.trim().length === 0) {
                              // Se não tiver token, gera um novo automaticamente
                              try {
                                await generateToken()
                                // generateToken já seta tokenAtivo = true e o token no form
                              } catch (error) {
                                // Se der erro, reverte o switch? O alert já é mostrado no service/hook
                                // Poderíamos forçar false aqui se falhar, mas o usuario pode tentar dnv clicando no botão
                              }
                            } else {
                              // Se já tem token, só ativa a flag
                              setEditUsuarioFormTokenAtivo({
                                tokenAtivo: true
                              })
                            }
                          } else {
                            // Se estiver desativando
                            setEditUsuarioFormTokenAtivo({
                              tokenAtivo: false
                            })
                            // Limpa o token conforme solicitado
                            setEditUsuarioFormToken({
                              token: ''
                            })
                          }
                        }}
                        name="tokenAtivo"
                        color="primary"
                      />
                      }
                      label="Token Ativo"
                    />
                    {(editUsuarioForm.fields.token || editUsuarioForm.fields.tokenAtivo) && (
                      <TextField
                        value={editUsuarioForm.fields.token || ''}
                        disabled={true}
                        name="token"
                        fullWidth
                        size="small"
                        id="token"
                        label="Token"
                        error={Boolean(editUsuarioForm.touched && editUsuarioForm.errors.find(item => item.key === 'token'))}
                        helperText={
                          editUsuarioForm.touched && editUsuarioForm.errors.find(item => item.key === 'token')?.message
                        }
                      />
                    )}
                    <Button
                      variant="outlined"
                      color="primary"
                      size="medium"
                      disabled={!canEdit}
                      onClick={async () => {
                        try {
                          await generateToken()
                        } catch (error: any) {
                          // Erro já é tratado no service
                        }
                      }}
                    >
                      Gerar Novo Token
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch
                    checked={editUsuarioForm.fields.ativo}
                    onChange={(e) => {
                      setEditUsuarioFormAtivo({
                        ativo: e.target.checked
                      })
                    }}
                    name="status"
                    color="primary"
                  />
                  }
                  label={editUsuarioForm.fields.ativo ? 'Usuário Ativo' : 'Usuário Inativo'}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!canEdit}
            >
              Editar Usuário
            </Button>
          </DialogActions>
        </Dialog>
      </>

  )
}